/**
 * MonitorStatusBadge - Visual indicator for monitor operational status
 *
 * Displays a color-coded badge representing the monitor operational status.
 * Color scheme:
 * - active: Green - Monitor is actively scanning
 * - paused: Yellow - Monitor is temporarily paused
 * - disabled: Gray - Monitor is disabled
 * - error: Red - Monitor encountered an error
 */

import React from 'react';
import { Badge } from '@/components/ui/badge';
import type { MonitorStatus } from '@/features/monitors/types/monitor.types';

interface MonitorStatusBadgeProps {
  /**
   * Monitor operational status to display
   */
  status: MonitorStatus;
  /**
   * Optional additional CSS classes
   */
  className?: string;
}

/**
 * Get custom color classes for monitor status badge
 */
const getColorClasses = (status: MonitorStatus): string => {
  switch (status) {
    case 'active':
      return 'bg-success/10 text-success border-success/30';
    case 'paused':
      return 'bg-warning/10 text-warning border-warning/30';
    case 'disabled':
      return 'bg-muted text-muted-foreground border-border';
    case 'error':
      return 'bg-destructive/10 text-destructive border-destructive/30';
    default:
      return '';
  }
};

/**
 * Get descriptive tooltip text for monitor status
 */
const getTooltipText = (status: MonitorStatus): string => {
  switch (status) {
    case 'active':
      return 'Monitor is actively scanning the directory';
    case 'paused':
      return 'Monitor is temporarily paused - no scans will run';
    case 'disabled':
      return 'Monitor is disabled and will not run';
    case 'error':
      return 'Monitor encountered an error during last scan';
    default:
      return '';
  }
};

/**
 * Get display label for status
 */
const getStatusLabel = (status: MonitorStatus): string => {
  switch (status) {
    case 'active':
      return 'Active';
    case 'paused':
      return 'Paused';
    case 'disabled':
      return 'Disabled';
    case 'error':
      return 'Error';
    default:
      return status;
  }
};

/**
 * MonitorStatusBadge Component
 *
 * Renders a styled badge indicating the monitor operational status.
 *
 * @example
 * ```tsx
 * <MonitorStatusBadge status="active" />
 * <MonitorStatusBadge status="error" className="ml-2" />
 * ```
 */
export const MonitorStatusBadge: React.FC<MonitorStatusBadgeProps> = ({ status, className }) => {
  const colorClasses = getColorClasses(status);
  const tooltipText = getTooltipText(status);
  const label = getStatusLabel(status);

  return (
    <Badge
      variant="outline"
      className={`${colorClasses} border font-semibold ${className || ''}`}
      title={tooltipText}
    >
      {label}
    </Badge>
  );
};
